import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import * as relativeTime from "dayjs/plugin/relativeTime";

import styles from "../../../styles/beneficios/BenefitInfo.module.scss";
import { If } from "../../components/control-statements";
import { Currency } from "../../utils/Currency";
import { Numbers } from "../../utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { useState } from "react";
import { Environment } from "shared/core/Environment";

const st = (className) => styles[className];

const days = [
  {
    key: 0,
    full: "Dom",
  },
  {
    key: 1,
    full: "Seg",
  },
  {
    key: 2,
    full: "Ter",
  },
  {
    key: 3,
    full: "Qua",
  },
  {
    key: 4,
    full: "Qui",
  },
  {
    key: 5,
    full: "Sex",
  },
  {
    key: 6,
    full: "Sab",
  },
];

const SingleUse = {
  0: "Múltiplos",
  1: "Único",
  2: "Contínuo",
  4: "Único por ambiente",
};

dayjs.locale("pt-br");
dayjs.extend(relativeTime);

export const BenefitInfo = ({ benefit }) => {
  const [partner, setPartner] = useState(null);

  const benefitName = Environment.id === 51 ? benefit.name : [benefit?.code, benefit?.pin].filter(Boolean).join(" - ");

  const loadPartner = async () => {
    const response = await OlgaEnvironmentsAPI.getPartners();
    const partners = response.getData([]);

    const partner = partners.find((p) => p.id == benefit.partner_id);
    setPartner(partner);
  };

  useState(() => {
    if (benefit) {
      loadPartner();
    }
  }, [benefit]);

  const mountTimes = (interval) => {
    const { start, end } = interval;

    const startTime = formatHourAndMinute(start);
    const endTime = formatHourAndMinute(end);

    return `${startTime}-${endTime}`;
  };

  function formatHourAndMinute(time) {
    const hour = time.slice(0, 2);
    const minute = time.slice(2);

    if (minute === "00") {
      return `${hour}h`;
    } else {
      return `${hour}:${minute}h`;
    }
  }

  const getTimes = (times) => {
    return times.map(mountTimes).join(", ");
  };

  return (
    <div className={st("details-benefit")}>
      <div className={st("benefit-code")}>
        <p>{benefitName}</p>
        <span>{getBenefitType(benefit.type)}</span>
      </div>

      <div className={st("step")}>
        <p>Descrição</p>
        <p>{benefit.description || "Não registrada"}</p>
      </div>

      <div className={st("step")}>
        <p>Imagem</p>
        {benefit.image ? (
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/${benefit.image}`}
            className={st("image")}
            alt="Imagem do benefício"
          />
        ) : (
          <p>Não registrada</p>
        )}
      </div>

      <div className={st("step")}>
        <p>Valor do benefício</p>
        <p>{getBenefitValue(benefit)}</p>
      </div>

      <If
        cond={["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(
          benefit.type
        )}
      >
        <div className={st("step")}>
          <p>SKU dos produtos selecionados</p>
          <p>{getBenefitProducts(benefit)}</p>
        </div>
      </If>

      <div className={st("step")}>
        <p>Pedido mínimo</p>
        <p>{Currency.formatCurrency(+(benefit.min_order || 0))}</p>
      </div>

      <If cond={benefit.expiration && benefit.expiration.gain}>
        <div className={st("step")}>
          <p>Expiração do benefício</p>
          <If cond={benefit?.expiration?.gain?.date}>
            <p>{dayjs(benefit?.expiration?.gain?.date).format("DD/MM/YYYY")}</p>
          </If>
          <If cond={benefit?.expiration?.gain?.days}>
            <p>{benefit?.expiration?.gain?.days} dias após</p>
          </If>
        </div>
      </If>

      <div className={st("step")}>
        <p>Tipo de uso</p>
        <p>{SingleUse[benefit.single_use] || "Múltiplos"}</p>
      </div>

      <div className={st("step")}>
        <p>Onde o benefício será utilizado</p>
        <p>{getUseType(benefit.type_use)}</p>
      </div>

      <div className={st("step")}>
        <p>Marcas para utilização</p>
        <p>
          {(benefit?.brands_valid || []).length > 0
            ? benefit?.brands_valid.map((brand) => brand.name).join(", ")
            : "Todas"}
        </p>
      </div>

      <div className={st("step")}>
        <p>Cliente pode adicionar</p>
        <p>{benefit?.grant_type === "MANUAL" ? "Não" : "Sim"}</p>
      </div>

      <If cond={benefit?.updated_at}>
        <div className={st("step")}>
          <p>Criado em</p>
          <p>
            {benefit?.created_by_name || "Desconhecido"} em{" "}
            {dayjs(benefit?.updated_at).format("DD/MM/YYYY HH:mm")}
          </p>
        </div>
      </If>

      <If cond={benefit?.visibility?.length > 0}>
        <div className={st("step")}>
          <p>Disponível para uso</p>
          <ul className={styles.visibility}>
            {benefit?.visibility?.map((v) => (
              <li key={v.weekDay}>
                {days[v.weekDay].full}: {getTimes(v.times)}
              </li>
            ))}
          </ul>
        </div>
      </If>
      <If cond={benefit?.partner_id}>
        <div className={st("step")}>
          <p>Convênio</p>
          <p>{partner?.name || "Desconhecido"}</p>
        </div>
      </If>
      <If cond={Environment.id == 34}>
        <div className={st("step")}>
          <p>Grupo</p>
          <p>{benefit?.extras?.group || "-"}</p>
        </div>
        <div className={st("step")}>
          <p>Justificativa</p>
          <p>{benefit?.extras?.justification || "-"}</p>
        </div>
      </If>
    </div>
  );
};

const benefitTypes = {
  CASHBACK: "CASHBACK",
  CASH_DISCOUNT: "CASH_DISCOUNT",
  PERCENTAGE_DISCOUNT: "PERCENTAGE_DISCOUNT",
  PRODUCT_DISCOUNT: "PRODUCT_DISCOUNT",
  PRODUCT_CASH_DISCOUNT: "PRODUCT_CASH_DISCOUNT",
  FREE_DELIVERY: "FREE_DELIVERY",
};

const getBenefitValue = (benefit) => {
  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";
  switch (benefit.type) {
    case benefitTypes.CASHBACK:
      return programByPoint
        ? `${benefit.value} pontos para cada R$100`
        : `${Numbers.formatFloat(benefit.value)}%`;
    case benefitTypes.PERCENTAGE_DISCOUNT:
    case benefitTypes.PRODUCT_DISCOUNT:
      return `${Numbers.formatFloat(benefit.value)}%`;
    case benefitTypes.FREE_DELIVERY:
      return "Entrega grátis";
    case benefitTypes.CASH_DISCOUNT:
    case benefitTypes.PRODUCT_CASH_DISCOUNT:
    default:
      return Currency.formatCurrency(benefit.value);
  }
};

const getBenefitType = (benefitType) => {
  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  const types = {
    CASHBACK: programByPoint ? "Pontos" : "Cashback",
    CASH_DISCOUNT: "Desconto em R$",
    PERCENTAGE_DISCOUNT: "Desconto em %",
    PRODUCT_DISCOUNT: "Desconto em % nos produtos selecionados",
    PRODUCT_CASH_DISCOUNT: "Desconto em R$ nos produtos selecionados",
    FREE_DELIVERY: "Entrega grátis",
  };

  let type = null;
  if (benefitType in types) {
    type = types[benefitType];
  } else {
    type = benefitType;
  }

  return type;
};

const getBenefitProducts = (benefit) => {
  if (benefit?.product_list?.length) {
    return benefit.product_list.join(", ");
  }
  return "Nenhum produto contemplado";
};

const USE_TYPES = {
  ONLINE: 1,
  OFFLINE: 2,
  TAKEAWAY: 4,
  FASTFILA: 8,
};

const isType = (haystack, needle) => (needle & haystack) >= needle;

const getUseType = (useType) => {
  const arr = [];

  if (isType(useType, USE_TYPES.ONLINE)) {
    arr.push("Delivery");
  }

  if (isType(useType, USE_TYPES.OFFLINE)) {
    arr.push("Salão");
  }

  if (isType(useType, USE_TYPES.TAKEAWAY)) {
    arr.push("Para viagem");
  }

  if (isType(useType, USE_TYPES.FASTFILA)) {
    arr.push("Consumo no local");
  }

  return arr.join(", ");
};
